import React from "react";
import {useNavigate} from "react-router-dom";
import {ChevronRightIcon} from "@heroicons/react/24/outline";
import {navigateWithChildTab, pickDetailTabTitle} from "../../../../components/pageTabs/pageTabsStorage";
import {
    useCompanyRelatedAssetData,
    useCompanyRelatedContractData,
    useCompanyRelatedUserData,
} from "./useCompanyRelatedInfoData";
import "../../../../components/detailLayout/detailLinkedList.css";
import "../companyManagePanel.css";

function RelatedListItem({title, subtitle, onClick}) {
    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
        }
    };

    return (
        <li
            className="asset-linked-list-item is-clickable company-related-info-item"
            onClick={onClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
        >
            <div className="asset-linked-list-content">
                <div className="asset-linked-list-title">{title || "\u00A0"}</div>
                <p className="company-related-info-sub">{subtitle || "\u00A0"}</p>
            </div>
            <ChevronRightIcon className="asset-linked-list-chevron" aria-hidden />
        </li>
    );
}

function RelatedColumn({title, rows, loading, total, renderItem}) {
    const isEmpty = !loading && rows.length === 0;

    return (
        <div className="company-related-info-column">
            <div className="company-related-info-column__head">
                <span className="company-related-info-column__title">{title}</span>
                <span
                    className={`company-related-info-column__count${total === 0 ? " is-zero" : ""}`}
                >
                    {loading ? "…" : total}
                </span>
            </div>

            <div
                className={`company-related-info-column__body${isEmpty ? " is-empty" : ""}${
                    loading ? " is-loading" : ""
                }`}
            >
                {loading ? (
                    <div className="company-related-info-empty" role="status">
                        불러오는 중…
                    </div>
                ) : isEmpty ? (
                    <div className="company-related-info-empty" role="status">
                        <span className="company-related-info-empty__icon" aria-hidden>
                            —
                        </span>
                        <span>등록된 항목이 없습니다</span>
                    </div>
                ) : (
                    <ul className="asset-linked-list company-related-info-list" role="list">
                        {rows.map((row, index) => renderItem(row, index))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function buildSubtitle(parts, separator = " · ") {
    return parts.map((part) => (part == null ? "" : String(part).trim())).filter(Boolean).join(separator);
}

function CompanyRelatedInfoPanel({id}) {
    const navigate = useNavigate();

    const users = useCompanyRelatedUserData(id);
    const assets = useCompanyRelatedAssetData(id);
    const contracts = useCompanyRelatedContractData(id);

    const openDetail = (row, fallbackTitle, urlPrefix) => {
        const rowId = row?.id;
        if (rowId == null) {
            return;
        }
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(row, fallbackTitle),
            url: `${urlPrefix}/${rowId}`,
        });
    };

    return (
        <div className="detail-view-tab-panel company-related-info-panel flex min-h-0 min-w-0 flex-1 flex-col w-full h-full">
            <div className="company-related-info-board">
                <RelatedColumn
                    title="직원정보"
                    rows={users.rows}
                    loading={users.loading}
                    total={users.total}
                    renderItem={(row) => (
                        <RelatedListItem
                            key={row.id}
                            title={row.name}
                            subtitle={buildSubtitle([row.position, row.email])}
                            onClick={() => openDetail(row, "사용자 상세", "/settings/userAccount/info")}
                        />
                    )}
                />
                <RelatedColumn
                    title="유지보수 자산"
                    rows={assets.rows}
                    loading={assets.loading}
                    total={assets.total}
                    renderItem={(row) => (
                        <RelatedListItem
                            key={row.id}
                            title={row.assetName}
                            subtitle={row.assetCode || undefined}
                            onClick={() => openDetail(row, "자산 상세", "/asset")}
                        />
                    )}
                />
                <RelatedColumn
                    title="계약정보"
                    rows={contracts.rows}
                    loading={contracts.loading}
                    total={contracts.total}
                    renderItem={(row) => (
                        <RelatedListItem
                            key={row.id}
                            title={row.name}
                            subtitle={buildSubtitle([row.classification, row.number], " | ")}
                            onClick={() => openDetail(row, "계약 상세", "/asset/contract")}
                        />
                    )}
                />
            </div>
        </div>
    );
}

export default CompanyRelatedInfoPanel;
